import React, { useState, useEffect, useContext, useMemo, useCallback, forwardRef } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./BillingPayment.scss";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { usePatientContext } from "../../context/PatientContext";
import { toLegacyPatient } from "../../context/normalizePatient";
import { CheckToken } from "../login/AccessToken";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MailIcon from "@mui/icons-material/Mail";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ClearIcon from "@mui/icons-material/Clear";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import StagingArea from "./StagingArea";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DisplayDate from "../../components/date/DisplayDate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InvoicePayment from "./InvoicePayment";
import InvoicePatientSearchDialog from "./InvoicePatientSearchDialog";

import { Accordion, AccordionSummary, AccordionDetails, Box, Card, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Grid, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import Navlogo from "../../assets/images/logo/NavLogo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import showConfirmDialog from "../../components/ShowConfirmDialog/ShowConfirmDialog";
import SideNavbar from "./SideNavbar";
import NoDataPage from "./NoDataPage";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { toast } from "react-toastify";
// import FooterPdfTemplateWithDept from "../Branding/FooterPdfTemplateWithDept";
import SaveAlt from "@mui/icons-material/SaveAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { displayPatientName } from "../../utils/displayPatientName";
import "jspdf-autotable";
import { fetchDepartmentSlipPdf } from "./departmentSlipApi";
import { printPdfBlob } from "../../utils/printPdfBlob";
// ── Shared styled calendar trigger (used in filter panel + standalone bar) ─
const CalendarTrigger = forwardRef(({ value, onClick, placeholder }, ref) => (
  <Box
    ref={ref}
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      px: 1.5,
      py: 0.7,
      cursor: "pointer",
      bgcolor: "white",
      minWidth: "155px",
      userSelect: "none",
      "&:hover": { borderColor: "#0E3F66" },
      "&:focus-within": { borderColor: "#0E3F66", outline: "2px solid #dbeafe" },
    }}
  >
    <CalendarMonthIcon sx={{ fontSize: 15, color: "#0E3F66", flexShrink: 0 }} />
    <Typography
      sx={{
        fontSize: "13px",
        color: value ? "#172935" : "#9ca3af",
        fontWeight: value ? 500 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {value || placeholder}
    </Typography>
  </Box>
));

function FilterChip({ label, onClear }) {
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5,
        bgcolor: "#dbeafe", border: "1px solid #93c5fd", borderRadius: "20px",
        px: 1.5, py: 0.4, fontSize: "12px", color: "#1e40af", fontWeight: 600,
      }}
    >
      {label}
      <ClearIcon
        sx={{ fontSize: 13, cursor: "pointer", ml: 0.25, "&:hover": { color: "#ef4444" } }}
        onClick={onClear}
      />
    </Box>
  );
}

const EMPTY_FILTERS = {
  fromDate: "", toDate: "",
  invoiceNumber: "",
  patientName: "",
  dueDateFrom: "", dueDateTo: "",
  minAmount: "", maxAmount: "",
  minPaid: "", maxPaid: "",
  minOutstanding: "", maxOutstanding: "",
  paymentStatus: "",
};

// ── Filter Modal ──────────────────────────────────────────────────────────
function FilterModal({ open, onClose, initialFilters, onApply }) {
  const [pending, setPending] = useState(EMPTY_FILTERS);

  useEffect(() => {
    if (open) setPending({ ...EMPTY_FILTERS, ...initialFilters });
  }, [open]);

  const toDateObj = (v) => (v ? new Date(v + "T00:00:00") : null);
  const toStr = (d) => (d instanceof Date && !isNaN(d) ? d.toISOString().split("T")[0] : "");
  const set = (key) => (val) => setPending((p) => ({ ...p, [key]: val }));

  const handleApply = () => { onApply(pending); onClose(); };
  const handleClearAll = () => setPending(EMPTY_FILTERS);

  const statusOptions = [
    { value: "", label: "All" },
    { value: "Paid", label: "Paid" },
    { value: "Partially Paid", label: "Partially Paid" },
    { value: "Pending", label: "Pending" },
  ];

  const sectionLabel = (text) => (
    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#0E3F66", mb: 1, textTransform: "uppercase", letterSpacing: "0.6px" }}>
      {text}
    </Typography>
  );

  const textInput = (key, placeholder) => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={pending[key]}
      onChange={(e) => set(key)(e.target.value)}
      fullWidth
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
    />
  );

  const numInput = (key, placeholder) => (
    <TextField
      size="small"
      type="number"
      placeholder={placeholder}
      value={pending[key]}
      onChange={(e) => set(key)(e.target.value)}
      fullWidth
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
    />
  );

  const dateRow = (fromKey, toKey) => (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "10px", color: "#6b7280", mb: 0.4, fontWeight: 600, textTransform: "uppercase" }}>From</Typography>
        <DatePicker
          selected={toDateObj(pending[fromKey])}
          onChange={(d) => set(fromKey)(toStr(d))}
          selectsStart
          startDate={toDateObj(pending[fromKey])}
          endDate={toDateObj(pending[toKey])}
          maxDate={toDateObj(pending[toKey]) || undefined}
          dateFormat="dd MMM yyyy"
          placeholderText="Start date"
          customInput={<CalendarTrigger placeholder="Start date" />}
          popperPlacement="bottom-start"
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "10px", color: "#6b7280", mb: 0.4, fontWeight: 600, textTransform: "uppercase" }}>To</Typography>
        <DatePicker
          selected={toDateObj(pending[toKey])}
          onChange={(d) => set(toKey)(toStr(d))}
          selectsEnd
          startDate={toDateObj(pending[fromKey])}
          endDate={toDateObj(pending[toKey])}
          minDate={toDateObj(pending[fromKey]) || undefined}
          dateFormat="dd MMM yyyy"
          placeholderText="End date"
          customInput={<CalendarTrigger placeholder="End date" />}
          popperPlacement="bottom-start"
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 3,
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "#f8fafc",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TuneIcon sx={{ color: "#0E3F66", fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#172935" }}>Filters</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280" }}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1, overflowY: "auto" }}>
        {/* Invoice Number */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Invoice Number")}
          {textInput("invoiceNumber", "Search invoice number...")}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Patient Name */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Patient Name")}
          {textInput("patientName", "Search patient name...")}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Invoice Date */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Invoice Date Range")}
          {dateRow("fromDate", "toDate")}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Due Date */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Due Date Range")}
          {dateRow("dueDateFrom", "dueDateTo")}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Gross Amount */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Gross Amount (₹)")}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>{numInput("minAmount", "Min amount")}</Box>
            <Box sx={{ flex: 1 }}>{numInput("maxAmount", "Max amount")}</Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Paid Amount */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Paid Amount (₹)")}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>{numInput("minPaid", "Min paid")}</Box>
            <Box sx={{ flex: 1 }}>{numInput("maxPaid", "Max paid")}</Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Outstanding */}
        <Box sx={{ mb: 2 }}>
          {sectionLabel("Outstanding Amount (₹)")}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>{numInput("minOutstanding", "Min outstanding")}</Box>
            <Box sx={{ flex: 1 }}>{numInput("maxOutstanding", "Max outstanding")}</Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Payment Status */}
        <Box sx={{ mb: 1 }}>
          {sectionLabel("Payment Status")}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {statusOptions.map((opt) => {
              const selected = pending.paymentStatus === opt.value;
              return (
                <Box
                  key={opt.label}
                  onClick={() => setPending((p) => ({ ...p, paymentStatus: opt.value }))}
                  sx={{
                    px: 2, py: 0.7,
                    borderRadius: "20px",
                    border: `1.5px solid ${selected ? "#0E3F66" : "#d1d5db"}`,
                    bgcolor: selected ? "#0E3F66" : "white",
                    color: selected ? "white" : "#374151",
                    fontSize: "13px",
                    fontWeight: selected ? 600 : 400,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    "&:hover": { borderColor: "#0E3F66", bgcolor: selected ? "#0E3F66" : "#f0f7ff" },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb", justifyContent: "space-between", bgcolor: "#f8fafc" }}>
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{ color: "#ef4444", textTransform: "none", fontSize: "13px", fontWeight: 500, "&:hover": { bgcolor: "#fef2f2" } }}
        >
          Clear All
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: "13px", borderColor: "#d1d5db", color: "#374151", px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: "13px", bgcolor: "#0E3F66", px: 2.5, fontWeight: 600, "&:hover": { bgcolor: "#0C3555" } }}
          >
            Apply Filters
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const getLocalDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function getCurrentUserId() {
  try {
    const token = sessionStorage.getItem("accessToken") || "";
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const raw = payload?.user_id ?? payload?.userId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

// Rename the component to Invoice
const Invoice = () => {
  const { userInfo } = usePermissions();
  const roleType = userInfo?.role_permission?.[0]?.role ?? "";
  const isBillingAdmin =
    roleType === "billingAdmin";

  const brandingData = useContext(BrandingContext);
  const history = useHistory();
  const location = useLocation();
  const { patient: activePatient } = usePatientContext();
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [facilityName, setFacilityName] = useState(
    sessionStorage.getItem("facility_name") || ""
  );
  const REACT_APP_SENDER_EMAIL = process.env.REACT_APP_SENDER_EMAIL;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // const [departments, setDepartments] = useState([]);
  const isEmailEnabled = sessionStorage.getItem("isEmailEnabled") !== "false";
  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
  }, []);
  // Update the route for adding a new invoice
  const handleAddItem = () => {
    if (selectedPatientFilter?.patient_id) {
      history.push({
        pathname: "/newInvoice",
        state: {
          preselectedInvoicePatient: selectedPatientFilter,
        },
      });
      return;
    }
    history.push("/newInvoice");
  };

  // Route for creating a new estimate, carrying the selected patient across
  const handleCreateEstimate = () => {
    if (selectedPatientFilter?.patient_id) {
      history.push({
        pathname: "/newEstimate",
        state: {
          preselectedEstimatePatient: selectedPatientFilter,
        },
      });
      return;
    }
    history.push("/newEstimate");
  };

  const [authToken, setAuthToken] = useState("");
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSearchText, setInvoiceSearchText] = useState("");
  const [debouncedInvoiceSearch, setDebouncedInvoiceSearch] = useState("");
  const [selectedPatientFilter, setSelectedPatientFilter] = useState(null);
  const [openPatientSearch, setOpenPatientSearch] = useState(false);
  const [invoicePagination, setInvoicePagination] = useState({
    page: 1,
    limit: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
    next_page: null,
    prev_page: null,
  });
  // DataGrid uses 0-based page index; backend uses 1-based.
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfDataUri, setPdfDataUri] = useState(null);

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [invoiceForPayment, setInvoiceForPayment] = useState(null);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");

  const applyFilters = useCallback((filters) => {
    setActiveFilters(filters);
    setFromDate(filters.fromDate || "");
    setToDate(filters.toDate || "");
    // Invoice Number / Patient Name here feed the same server-side `search`
    // the top search box uses, so a match on another page is actually found —
    // previously these two only filtered rows already on the current page.
    const modalSearchText = (filters.invoiceNumber || filters.patientName || "").trim();
    if (modalSearchText) {
      // selectedPatientId takes priority over searchText in fetchInvoiceList,
      // so a lingering "select patient" filter would otherwise silently
      // override this search.
      setSelectedPatientFilter(null);
      setInvoiceSearchText(modalSearchText);
    }
  }, []);

  const clearFilterKey = useCallback((keys) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { next[k] = ""; });
      return next;
    });
    if (keys.includes("fromDate")) setFromDate("");
    if (keys.includes("toDate")) setToDate("");
    if (keys.includes("invoiceNumber") || keys.includes("patientName")) {
      setInvoiceSearchText("");
    }
  }, []);

  const dashboardDate = useMemo(
    () => new Date().toISOString().split("T")[0],
    []
  );
  const [dashboardInvoices, setDashboardInvoices] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [collectionReportFromDate, setCollectionReportFromDate] = useState(getLocalDateStr);
  const [collectionReportToDate, setCollectionReportToDate] = useState(getLocalDateStr);
  const [collectionReportLoading, setCollectionReportLoading] = useState(false);
  const [collectionReportData, setCollectionReportData] = useState(null);
  const [collectionDashboardLoading, setCollectionDashboardLoading] = useState(false);
  const [collectionDashboardOpen, setCollectionDashboardOpen] = useState(false);

  const handlePayNow = (row) => {
    setInvoiceForPayment(row);
    setOpenPaymentModal(true);
  };

  const isSettlementPayment = (payment) => {
    const paymentType = String(payment?.payment_type || "").toUpperCase();
    if (paymentType === "REFUND") return false;
    const status = String(payment?.status || "").toUpperCase();
    const blocked = new Set([
      "FAILED",
      "CANCELLED",
      "VOID",
      "REJECTED",
      "REVERSED",
    ]);
    if (blocked.has(status)) return false;
    if (
      status === "" ||
      status === "SUCCESS" ||
      status === "PARTIALLY PAID" ||
      status === "PARTIALLY_PAID" ||
      status === "PAID" ||
      status === "PENDING" ||
      status === "COMPLETED"
    ) {
      return true;
    }
    if (
      paymentType === "PARTIAL" ||
      paymentType === "FULL" ||
      paymentType === "PARTIAL PAYMENT"
    ) {
      return true;
    }
    return false;
  };

  const getInvoicePaidAmount = (row) => {
    // partial_payment is the authoritative net-paid figure — payment_details/
    // and refund_payment/ both keep it correctly reduced on refund. Previously
    // this took whichever was larger between partial_payment and the summed
    // non-refund payment_details, which re-inflated the paid amount back to
    // the pre-refund gross total for any invoice that ever had a refund
    // (the historical payment rows don't disappear, only the refund's own
    // row is excluded from that sum — the sum itself is never reduced).
    if (row?.partial_payment !== null && row?.partial_payment !== undefined) {
      return parseFloat(row.partial_payment) || 0;
    }
    if (row?.amount_paid !== null && row?.amount_paid !== undefined) {
      return parseFloat(row.amount_paid) || 0;
    }
    if (Array.isArray(row?.payment_details) && row.payment_details.length > 0) {
      const settlementPayments = row.payment_details.filter(isSettlementPayment);
      return settlementPayments.reduce(
        (sum, p) => sum + Number(p?.payment_amount ?? 0),
        0,
      );
    }
    return 0;
  };

  // Settled refunds (REFUND rows in payment_details). refund_payment/ lowers
  // partial_payment by this amount but never lowers total_amount, so the
  // refunded rupees are no longer owed and must come off the outstanding.
  const getInvoiceRefundedAmount = (row) => {
    if (!Array.isArray(row?.payment_details)) return 0;
    return row.payment_details.reduce((sum, p) => {
      if (String(p?.payment_type || "").toUpperCase() !== "REFUND") return sum;
      const status = String(p?.status || "").toUpperCase();
      if (status && status !== "SUCCESS") return sum;
      return sum + (Number(p?.payment_amount ?? 0) || 0);
    }, 0);
  };

  // Gross collected from the patient, before refunds. partial_payment is net
  // of settled refunds, so add them back for the "Paid Amount" column; the
  // refund itself is shown in its own column.
  const getInvoiceCollectedAmount = (row) =>
    getInvoicePaidAmount(row) + getInvoiceRefundedAmount(row);

  // What the patient still owes: total, less refunds, less what is paid.
  const getInvoiceNetTotal = (row) => {
    const total = parseFloat(row?.total_amount || 0);
    const net = total - getInvoiceRefundedAmount(row);
    return net > 0 ? net : 0;
  };

  const getInvoiceOutstanding = (row) => {
    const paid = getInvoicePaidAmount(row);
    const diff = getInvoiceNetTotal(row) - paid;
    return diff > 0.001 ? diff : 0;
  };

  const formatInr = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getInvoiceItems = (row) =>
    Array.isArray(row?.items) ? row.items : [];

  const getInvoiceDiscountPercent = (row) => {
    const items = getInvoiceItems(row);
    if (!items.length) return 0;
    const grossTotal = items.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      const price = Number(item?.item_price || item?.price || 0);
      return sum + qty * price;
    }, 0);
    if (grossTotal <= 0) return 0;
    const discountTotal = items.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      const price = Number(item?.item_price || item?.price || 0);
      const base = qty * price;
      const pct = Number(item?.discount_percentage || 0);
      return sum + (base * pct) / 100;
    }, 0);
    return (discountTotal / grossTotal) * 100;
  };


  // const handleSendMail = (id) => {
  //   const foundEstimate = invoiceList.find((invoice) => invoice.invoice_id === id);

  //   if (foundEstimate) {
  //     setSelectedInvoice(foundEstimate);

  //   } else {
  //     console.error(`Invoice with id ${id} not found in invoice list`);
  //   }
  // };
  const handleSendMail = async (id) => {
    const message = `Are you sure you want to send this invoice via mail?`;
    const isConfirmed = await showConfirmDialog(message);

    if (isConfirmed) {
      const foundInvoice = invoiceList.find(
        (invoice) => invoice.invoice_id === id
      );

      if (foundInvoice) {
        setSelectedInvoice(foundInvoice);
      } else {
        console.error(`Invoice with id ${id} not found in invoice list`);
      }
    } else {
      console.log("Sending email canceled");
    }
  };
  // const fetchDepartments = async () => {
  //   try {
  //     const headers = { Authorization: `Bearer ${authToken}` };
  //     const response = await axios.get(
  //       `${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_departments/`,
  //       { headers }
  //     );
  //     setDepartments(response.data.department_list);
  //   } catch (error) {
  //     console.error("Error fetching departments", error);
  //   }
  // };

  // useEffect(() => {
  //   fetchDepartments();
  // }, [authToken]);
  useEffect(() => {
    if (selectedInvoice) {
      generateAndDownloadPDF();
    }
  }, [selectedInvoice]);
  useEffect(() => {
    if (pdfDataUri) {
      sendEmail();
    }
  }, [pdfDataUri]);

  const sendEmail = () => {
    try {
      console.log("Preparing to send email...");
      let bannerBase64 = null;

      const bannerObject = brandingData.find(
        (item) => item.name === "email_banner" && item.type === "image"
      );

      if (bannerObject) {
        bannerBase64 = bannerObject.content; // Get the base64 content
      } else {
        console.error("Banner image not found in branding data");
        toast.error("Failed to find the banner image.");
        return;
      }
      const base64Data = pdfDataUri.split(",")[1];

      const emailData = {
        SENDER: REACT_APP_SENDER_EMAIL,
        SUBJECT: "New Invoice Created Successfully",
        RECIPIENT: selectedInvoice.patient_email,
        ATTACHMENT: [
          {
            FILE: base64Data,
            FILENAME: "attach.pdf",
          },
        ],
        TYPE: "INVOICE",
        BANNER: bannerBase64,
      };
      // if (
      //   sessionStorage.getItem("isEmailEnabled") === "true" &&
      //   selectedInvoice.patient_email
      // ) {
      //   const response = axios.post(
      //     `${REACT_APP_BACKEND_ROUTE_URL}send_email/`,
      //     emailData,
      //     {
      //       headers: {
      //         Authorization: `Bearer ${authToken}`,
      //       },
      //     }
      //   );

      //   console.log("Email sent successfully", response.data);
      // } else {
      //   toast.error("Email service not activated for this license");
      // }
      if (sessionStorage.getItem("isEmailEnabled") !== "true") {
        toast.error("Email service not activated for this license");
      } else if (
        !selectedInvoice?.patient_email ||
        typeof selectedInvoice.patient_email !== "string" ||
        selectedInvoice.patient_email.trim() === ""

      ) {
        toast.error("Email not sent as patient's email address is unavailable");
      } else {
        axios
          .post(`${REACT_APP_BACKEND_ROUTE_URL}send_email/`, emailData, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          .then((response) => {
            console.log("Email sent successfully", response.data);
            toast.success("Email Sent Successfully");
          })
          .catch((error) => {
            console.error("Error sending email", error);
            toast.error("Error sending email. Please try again.");
          });
      }
    } catch (error) {
      console.error("Error sending email", error);
    }
  };

  // const generateAndDownloadPDF = async () => {
  //   console.log("inside gen pdf");
  //   const input = document.getElementById("invoice-template");
  //   html2canvas(input, { useCORS: true, allowTaint: true, scrollY: 0 }).then(
  //     (canvas) => {
  //       const image = { type: "jpeg", quality: 0.98 };

  //       var pdf = new jsPDF("p", "in", [8.5, 11]);
  //       pdf.addImage(
  //         canvas.toDataURL("image/jpeg", 1.0),
  //         "JPEG",
  //         0,
  //         0,
  //         8.5,
  //         11
  //       );

  //       setPdfDataUri(pdf.output("datauri"));
  //     }
  //   );
  // };

  const generateAndDownloadPDF = async () => {
    const input = document.getElementById("invoice-template");

    // Added scale to improve resolution and background to remove transparency issues
    html2canvas(input, {
      useCORS: true,
      allowTaint: true,
      scrollY: 0,
      backgroundColor: "#ffffff",
      scale: 2,
    }).then((canvas) => {
      const image = { type: "jpeg", quality: 0.98 };
      const margin = [0.5, 0.5];

      var imgWidth = 8.5;
      var pageHeight = 11;

      var innerPageWidth = imgWidth - margin[0] * 2;
      var innerPageHeight = pageHeight - margin[1] * 2;

      var pxFullHeight = canvas.height;
      var pxPageHeight = Math.floor(canvas.width * (pageHeight / imgWidth));
      var nPages = Math.ceil(pxFullHeight / pxPageHeight);

      var pageHeight = innerPageHeight;

      var pageCanvas = document.createElement("canvas");
      var pageCtx = pageCanvas.getContext("2d");
      pageCanvas.width = canvas.width;
      pageCanvas.height = pxPageHeight;

      var pdf = new jsPDF("p", "in", [8.5, 11]);

      for (var page = 0; page < nPages; page++) {
        if (page === nPages - 1 && pxFullHeight % pxPageHeight !== 0) {
          pageCanvas.height = pxFullHeight % pxPageHeight;
          pageHeight = (pageCanvas.height * innerPageWidth) / pageCanvas.width;
        }

        var w = pageCanvas.width;
        var h = pageCanvas.height;
        pageCtx.fillStyle = "white"; // Ensure a white background on each canvas page
        pageCtx.fillRect(0, 0, w, h);
        pageCtx.drawImage(canvas, 0, page * pxPageHeight, w, h, 0, 0, w, h);

        if (page > 0) pdf.addPage();
        var imgData = pageCanvas.toDataURL(
          "image/" + image.type,
          image.quality
        );
        pdf.addImage(
          imgData,
          image.type,
          margin[1],
          margin[0],
          innerPageWidth,
          pageHeight
        );
      }

      setPdfDataUri(pdf.output("datauri"));
      pdf.save(`Invoice.pdf`);
    });
  };

  useEffect(() => {
    const check = CheckToken();
    console.log(check);
    setAuthToken(sessionStorage.getItem("accessToken") ?? "");
  }, []); // Run only once on component mount

  useEffect(() => {
    // Prefer the patient passed via navigation state; otherwise fall back to the
    // app-wide active patient (persisted in PatientContext) so the filter
    // survives back-navigation and direct entry as long as a patient is selected.
    const fromRoute = location?.state?.preselectedInvoicePatient;
    const preselected =
      fromRoute ||
      (activePatient?.patient_id ? toLegacyPatient(activePatient) : null);
    if (preselected && (preselected.patient_id || preselected.pat_id)) {
      const resolvedPatientId =
        preselected.patient_id || preselected.pat_id;
      setSelectedPatientFilter({
        ...preselected,
        patient_id: resolvedPatientId,
      });
      setInvoiceSearchText("");
      // Clear the route state so a manual refresh doesn't re-apply it (the
      // context fallback keeps the filter after refresh anyway).
      if (fromRoute) {
        history.replace({
          pathname: location.pathname,
          search: location.search,
          state: {},
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInvoiceSearch(invoiceSearchText.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [invoiceSearchText]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [debouncedInvoiceSearch]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [selectedPatientFilter?.patient_id]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [
    activeFilters.dueDateFrom,
    activeFilters.dueDateTo,
    activeFilters.minAmount,
    activeFilters.maxAmount,
    activeFilters.minPaid,
    activeFilters.maxPaid,
    activeFilters.minOutstanding,
    activeFilters.maxOutstanding,
    activeFilters.paymentStatus,
  ]);

  const handleSelectPatientFilter = (patient) => {
    const resolvedPatientId = patient?.patient_id || patient?.pat_id;
    if (!resolvedPatientId) return;
    const normalizedPatient = { ...patient, patient_id: resolvedPatientId };
    setSelectedPatientFilter(normalizedPatient);
    setInvoiceSearchText("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchInvoiceList({
      page: 1,
      limit: paginationModel.pageSize,
      selectedPatientId: resolvedPatientId,
    });
  };

  const fetchInvoiceList = async ({
    page,
    limit,
    searchText = debouncedInvoiceSearch,
    selectedPatientId = selectedPatientFilter?.patient_id,
    isMounted = true,
    dateFrom = fromDate,
    dateTo   = toDate,
    filters  = activeFilters,
  } = {}) => {
    if (!authToken) return;

    const safePage = Number.isFinite(page) ? page : 1;
    const safeLimit = Number.isFinite(limit) ? limit : 10;

    const requestData = {
      hip_id: facilityName,
      // Tell backend to apply pagination + return pagination object
      is_pagination_required: true,
      page: safePage,
      limit: safeLimit,
    };
    if (selectedPatientId) {
      requestData.search = String(selectedPatientId).trim();
    } else if (searchText?.trim()?.length >= 2) {
      requestData.search = searchText.trim();
    }
    if (dateFrom) requestData.from_date = dateFrom;
    if (dateTo)   requestData.to_date   = dateTo;
    // Filters modal fields below now go server-side too, so a match on
    // another page is actually found instead of only narrowing whatever the
    // current page happened to fetch.
    if (filters?.dueDateFrom) requestData.due_date_from = filters.dueDateFrom;
    if (filters?.dueDateTo)   requestData.due_date_to   = filters.dueDateTo;
    if (filters?.minAmount !== "" && filters?.minAmount != null) requestData.min_amount = filters.minAmount;
    if (filters?.maxAmount !== "" && filters?.maxAmount != null) requestData.max_amount = filters.maxAmount;
    if (filters?.minPaid !== "" && filters?.minPaid != null) requestData.min_paid = filters.minPaid;
    if (filters?.maxPaid !== "" && filters?.maxPaid != null) requestData.max_paid = filters.maxPaid;
    if (filters?.minOutstanding !== "" && filters?.minOutstanding != null) requestData.min_outstanding = filters.minOutstanding;
    if (filters?.maxOutstanding !== "" && filters?.maxOutstanding != null) requestData.max_outstanding = filters.maxOutstanding;
    if (filters?.paymentStatus) requestData.payment_status = filters.paymentStatus;

    setInvoiceLoading(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_all_invoices/`,
        requestData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (!isMounted) return;

      const invoices = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
      const pg = res?.data?.pagination || {};

      setInvoicePagination({
        page: Number(pg.page) || safePage,
        limit: Number(pg.limit) || safeLimit,
        total_records: Number(pg.total_records) || 0,
        total_pages: Number(pg.total_pages) || 0,
        has_next: Boolean(pg.has_next),
        has_prev: Boolean(pg.has_prev),
        next_page: pg.next_page ?? null,
        prev_page: pg.prev_page ?? null,
      });

      // Backend already filters active + orders by latest invoice_date.
      const invoicesWithId = invoices.map((invoice) => ({
        ...invoice,
        id: invoice.invoice_id,
        total: parseFloat(invoice.total_amount).toFixed(2),
      }));

      setInvoiceList(invoicesWithId);
    } catch (error) {
      console.error("Error fetching invoice list:", error);
    } finally {
      if (isMounted) setInvoiceLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const trimmedDebouncedSearch = debouncedInvoiceSearch.trim();
    if (trimmedDebouncedSearch.length === 1) {
      return () => {
        isMounted = false;
      };
    }
    const backendPage = paginationModel.page + 1;
    const backendLimit = paginationModel.pageSize;
    fetchInvoiceList({
      page: backendPage,
      limit: backendLimit,
      searchText: trimmedDebouncedSearch,
      isMounted,
    });
    return () => {
      isMounted = false;
    };
  }, [
    authToken,
    facilityName,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedInvoiceSearch,
    selectedPatientFilter?.patient_id,
    fromDate,
    toDate,
    activeFilters.dueDateFrom,
    activeFilters.dueDateTo,
    activeFilters.minAmount,
    activeFilters.maxAmount,
    activeFilters.minPaid,
    activeFilters.maxPaid,
    activeFilters.minOutstanding,
    activeFilters.maxOutstanding,
    activeFilters.paymentStatus,
  ]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [fromDate, toDate]);

  const handleEdit = (id) => {
    // Find the selected invoice based on the id
    const selectedInvoice = invoiceList.find(
      (invoice) => invoice.invoice_id === id
    );

    // Log the selected invoice and the entire invoiceList to the console
    console.log("Selected Invoice:", selectedInvoice);
    console.log("Complete Invoice List:", invoiceList);

    const modifiedData = {
      ...selectedInvoice,
      // patient_id: selectedInvoice.customer, // Assuming customer_id is the patient_id
      // name: selectedInvoice.customer_name, // Assuming name is the patient name
      patient_id: selectedInvoice.patient_id, // Assuming customer_id is the patient_id
      name: selectedInvoice.patient_name, // Assuming name is the patient name
    };

    // Navigate to the edit page and pass the invoice data in the location state
    history.push({
      pathname: `/editInvoice/${id}`,
      state: { data: modifiedData },
    });
  };

  const handleView = (id) => {
    const selectedInvoice = invoiceList.find(
      (invoice) => invoice.invoice_id === id || invoice.id === id
    );

    if (selectedInvoice) {
      history.push({
        pathname: `/viewInvoice/${id}`,
        state: { data: selectedInvoice },
      });
    } else {
      console.error(`Invoice with id ${id} not found in invoiceList`);
    }
  };

  const handlePrintDepartmentSlips = async (id) => {
    try {
      const pdf = await fetchDepartmentSlipPdf(id);
      if (!pdf) {
        toast.info("No department slips for this invoice");
        return;
      }
      await printPdfBlob(pdf);
    } catch (error) {
      toast.error(error.message || "Could not print department slips");
    }
  };

  const handleDelete = async (id) => {
    const message = `Are you sure you want to delete this invoice?`;
    const isConfirmed = await showConfirmDialog(message);

    if (isConfirmed) {
      console.log(`Deleting invoice with id: ${id}`);

      // Make an HTTP POST request for deletion
      const headers = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json", // Specify the content type as JSON
      };

      const requestData = {
        invoice_id: id,
      };

      const requestConfig = {
        headers: headers,
      };

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}delete_invoice/`,
          JSON.stringify(requestData),
          requestConfig
        )
        .then(() => {
          console.log("Invoice deleted successfully!");
          fetchInvoiceList();
          // Implement additional logic after successful deletion if needed
        })
        .catch((error) => {
          console.error("Error deleting invoice:", error);
          // Implement error handling logic
        });
    } else {
      console.log("Deletion canceled");
    }
  };

  const handleConvertToEstimate = async (id) => {
    const message = `Are you sure you want to convert this invoice to an estimate?`;
    const isConfirmed = await showConfirmDialog(message);
    if (isConfirmed) {
      console.log(`Converting invoice with id ${id} to an estimate`);
      // Implement the logic to convert invoice to estimate
    } else {
      console.log("Conversion canceled");
    }
  };
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };
  const getRowClassName = (params) => {
    return "custom-row";
  };

  const brandingItem = brandingData?.find(
    (item) => item.name === "branding_statements"
  );
  const brandingContent = brandingItem?.content || null;

  // Extract the 'collateralBrandStatement' and 'themeColour' from JSON
  const collateralBrandStatement =
    brandingContent?.collateralBrandStatement || null;
  const themeColour = brandingContent?.themeColour || "#21cdc0";

  // Find the 'collateral_image.png' and get its base64 content directly
  const collateralImageItem = brandingData?.find(
    (item) => item.name === "collateral_image"
  );
  const collateralImage = collateralImageItem?.content || null;

  // Find the 'email_banner.png' and get its base64 content directly
  const emailBannerItem = brandingData?.find(
    (item) => item.name === "email_banner"
  );
  const emailBanner = emailBannerItem?.content || null;

  const getPaymentStatus = (inv) => {
    const total = getInvoiceNetTotal(inv);
    const paid = getInvoicePaidAmount(inv);
    if (paid >= total - 0.01 && total > 0) return "Paid";
    if (paid >= 1) return "Partially Paid";
    return "Pending";
  };

  const filteredInvoiceList = useMemo(() => {
    let result = invoiceList;
    const f = activeFilters;

    if (f.invoiceNumber) {
      const s = f.invoiceNumber.toLowerCase();
      result = result.filter((r) => String(r.invoice_number || "").toLowerCase().includes(s));
    }
    if (f.patientName) {
      const s = f.patientName.toLowerCase();
      result = result.filter((r) => String(displayPatientName(r.patient_name) || "").toLowerCase().includes(s));
    }
    if (f.dueDateFrom) result = result.filter((r) => (r.due_date || "") >= f.dueDateFrom);
    if (f.dueDateTo)   result = result.filter((r) => (r.due_date || "") <= f.dueDateTo);
    if (f.minAmount !== "") {
      const min = Number(f.minAmount);
      if (!isNaN(min)) result = result.filter((r) => Number(r.total_amount || 0) >= min);
    }
    if (f.maxAmount !== "") {
      const max = Number(f.maxAmount);
      if (!isNaN(max)) result = result.filter((r) => Number(r.total_amount || 0) <= max);
    }
    // minPaid/maxPaid/minOutstanding/maxOutstanding/paymentStatus are NOT
    // re-applied here — they're now fully server-side (get_all_invoices
    // filters on the authoritative `partial_payment` field). Re-checking them
    // here with getInvoicePaidAmount()'s own "max of partial_payment or
    // summed payment_details" heuristic actively hid correctly-filtered rows
    // for any invoice that ever had a refund: partial_payment is reduced by
    // the refund (correct), but the historical payment_details sum still
    // reflects the pre-refund total, and getInvoicePaidAmount taking the max
    // of the two re-inflated the paid amount — e.g. an invoice reduced to
    // "Partially Paid" by a refund still computed as "Paid" here and was
    // filtered back out after the backend had already correctly included it.
    return result;
  }, [invoiceList, activeFilters]);

  const activeFilterCount = [
    activeFilters.fromDate || activeFilters.toDate,
    activeFilters.invoiceNumber,
    activeFilters.patientName,
    activeFilters.dueDateFrom || activeFilters.dueDateTo,
    activeFilters.minAmount || activeFilters.maxAmount,
    activeFilters.minPaid || activeFilters.maxPaid,
    activeFilters.minOutstanding || activeFilters.maxOutstanding,
    activeFilters.paymentStatus,
  ].filter(Boolean).length;

  const getExportRows = useCallback(() => {
    return filteredInvoiceList;
  }, [filteredInvoiceList]);

  const generatePdf = () => {
    const rows = getExportRows();
    const pageW = 297; // A4 landscape width in mm
    const pageH = 210;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

    // ── Header band ──────────────────────────────────────────────────
    doc.setFillColor(18, 83, 134);
    doc.rect(0, 0, pageW, 22, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("Billing Invoice Report", 12, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Generated: ${today}`, pageW - 12, 10, { align: "right" });
    doc.text(`Facility: ${facilityName || "—"}`, pageW - 12, 16, { align: "right" });

    // ── Sub-header meta row ──────────────────────────────────────────
    doc.setFillColor(235, 244, 255);
    doc.rect(0, 22, pageW, 10, "F");
    doc.setTextColor(18, 83, 134);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(`Total Records: ${rows.length}`, 12, 28.5);

    const totalGross = rows.reduce((s, inv) => s + Number(inv.total_amount || 0), 0);
    const totalPaid  = rows.reduce((s, inv) => s + getInvoiceCollectedAmount(inv), 0);
    const totalOut   = rows.reduce((s, inv) => s + getInvoiceOutstanding(inv), 0);
    doc.text(`Gross: Rs.${formatInr(totalGross)}`, 70, 28.5);
    doc.text(`Paid: Rs.${formatInr(totalPaid)}`, 140, 28.5);
    doc.setTextColor(153, 27, 27);
    doc.text(`Outstanding: Rs.${formatInr(totalOut)}`, 210, 28.5);

    // ── Table ────────────────────────────────────────────────────────
    const statusColor = (status) => {
      if (status === "Paid") return [6, 95, 70];
      if (status === "Partially Paid") return [154, 52, 18];
      return [153, 27, 27];
    };

    doc.autoTable({
      startY: 34,
      head: [[
        "Invoice No.", "Patient Name", "Invoice Date",
        "Due Date", "Gross (Rs.)", "Discount (%)",
        "Paid (Rs.)", "Refunded (Rs.)", "Outstanding (Rs.)", "Status",
      ]],
      body: rows.map((inv) => [
        inv.invoice_number || "—",
        displayPatientName(inv.patient_name),
        inv.invoice_date || "—",
        inv.due_date || "—",
        formatInr(Number(inv.total_amount || 0)),
        Number(getInvoiceDiscountPercent(inv)).toFixed(2),
        formatInr(getInvoiceCollectedAmount(inv)),
        formatInr(getInvoiceRefundedAmount(inv)),
        formatInr(getInvoiceOutstanding(inv)),
        getPaymentStatus(inv),
      ]),
      headStyles: {
        fillColor: [18, 83, 134],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
        cellPadding: 3,
        halign: "center",
      },
      alternateRowStyles: { fillColor: [240, 247, 255] },
      bodyStyles: { fontSize: 7.5, textColor: [55, 65, 81], cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 36 },
        1: { cellWidth: 38 },
        2: { cellWidth: 24, halign: "center" },
        3: { cellWidth: 22, halign: "center" },
        4: { cellWidth: 24, halign: "right" },
        5: { cellWidth: 20, halign: "right" },
        6: { cellWidth: 24, halign: "right" },
        7: { cellWidth: 24, halign: "right" },
        8: { cellWidth: 26, halign: "right" },
        9: { cellWidth: 22, halign: "center" },
      },
      margin: { top: 34, left: 10, right: 10 },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 9) {
          const status = data.cell.raw;
          const [r, g, b] = statusColor(status);
          data.cell.styles.textColor = [r, g, b];
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: (data) => {
        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text(
          `Page ${data.pageNumber} of ${totalPages}  •  Hlthtek Billing Report`,
          pageW / 2,
          pageH - 5,
          { align: "center" }
        );
        // Top header on every page except first
        if (data.pageNumber > 1) {
          doc.setFillColor(18, 83, 134);
          doc.rect(0, 0, pageW, 10, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.text("Billing Invoice Report (cont.)", 12, 7);
        }
      },
    });

    // ── Summary box ──────────────────────────────────────────────────
    const finalY = doc.lastAutoTable.finalY;
    if (finalY < pageH - 28) {
      doc.setDrawColor(18, 83, 134);
      doc.setLineWidth(0.4);
      doc.line(10, finalY + 5, pageW - 10, finalY + 5);

      doc.setFillColor(235, 244, 255);
      doc.roundedRect(pageW - 110, finalY + 8, 100, 22, 2, 2, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(18, 83, 134);
      doc.text("Export Summary", pageW - 60, finalY + 14, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(55, 65, 81);
      doc.text(`Total Gross: Rs.${formatInr(totalGross)}`, pageW - 108, finalY + 20);
      doc.text(`Total Paid: Rs.${formatInr(totalPaid)}`, pageW - 108, finalY + 26);
      doc.setTextColor(153, 27, 27);
      doc.text(`Outstanding: Rs.${formatInr(totalOut)}`, pageW - 60, finalY + 20);
    }

    doc.save(`Invoice_Report_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const downloadCsv = () => {
    const rows = getExportRows();
    const headers = [
      "Invoice Number", "Patient Name", "Invoice Date", "Due Date",
      "Gross Amount", "Discount (%)", "Paid Amount", "Refunded Amount", "Outstanding", "Payment Status",
    ];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csvLines = [
      headers.map(escape).join(","),
      ...rows.map((inv) => [
        escape(inv.invoice_number || ""),
        escape(displayPatientName(inv.patient_name)),
        escape(inv.invoice_date || ""),
        escape(inv.due_date || ""),
        Number(inv.total_amount || 0).toFixed(2),
        Number(getInvoiceDiscountPercent(inv)).toFixed(2),
        formatInr(getInvoiceCollectedAmount(inv)),
        formatInr(getInvoiceRefundedAmount(inv)),
        formatInr(getInvoiceOutstanding(inv)),
        escape(getPaymentStatus(inv)),
      ].join(",")),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice_Report_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const applyCollectionReport = async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      toast.error("Unable to identify current user.");
      return;
    }
    if (!collectionReportFromDate || !collectionReportToDate) {
      toast.error("Please select both From Date and To Date.");
      return;
    }
    if (collectionReportFromDate > collectionReportToDate) {
      toast.error("From Date cannot be greater than To Date.");
      return;
    }
    setCollectionDashboardLoading(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}collection_report/`,
        { hip_id: facilityName, user_id: userId, from_date: collectionReportFromDate, to_date: collectionReportToDate },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data?.status !== "Success") {
        toast.error(res.data?.message || "Failed to fetch collection data.");
        return;
      }
      setCollectionReportData(res.data);
    } catch (err) {
      console.error("Collection report apply error:", err);
      toast.error(err?.response?.data?.message || "Failed to fetch collection data.");
    } finally {
      setCollectionDashboardLoading(false);
    }
  };

  const downloadCollectionReportPdf = () => {
    if (!collectionReportData) {
      toast.error("Please click Apply to load collection data first.");
      return;
    }

    setCollectionReportLoading(true);
    try {
      const { summary, invoices = [], refunds = [], date_range } = collectionReportData;
      const pdfFromDate = date_range?.from_date || collectionReportFromDate;
      const pdfToDate = date_range?.to_date || collectionReportToDate;

      const pageW = 210;
      const pageH = 297;
      const marginL = 12;
      const marginR = 12;
      const tealBandH = 22;
      const headerH = 32;
      const tableTopMargin = headerH + 4;
      const footerH = 12;

      const facilityDisplayName =
        sessionStorage.getItem("facility_display_name") || facilityName || "Facility";

      const fmtDate = (s) => {
        if (!s) return "—";
        const d = new Date(s.includes("T") ? s : s + "T00:00:00");
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      };

      // Format ISO timestamp → local "YYYY-MM-DD" (date only, no time)
      const fmtDateTime = (s) => {
        if (!s) return "—";
        const d = new Date(s);
        if (isNaN(d.getTime())) return s.split("T")[0] || "—";
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };

      const fmtCur = (v) =>
        `Rs.${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

      const now = new Date();
      const generatedDT =
        now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
        " " +
        now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const collReportLogoUrl = collateralImage
        ? `data:image/png;base64,${collateralImage}`
        : null;

      const drawPageHeader = () => {
        // ── Teal band (identical to invoice template) ───────────────────
        doc.setFillColor(33, 205, 192);
        doc.rect(0, 0, pageW, tealBandH, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(facilityDisplayName, marginL, 12);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("Collection Report", marginL, 19);

        // Logo — right side of teal band
        if (collReportLogoUrl) {
          try {
            const imgProps = doc.getImageProperties(collReportLogoUrl);
            const maxW = 42, maxH = 14;
            const ratio = imgProps.width / imgProps.height;
            let w = maxW, h = w / ratio;
            if (h > maxH) { h = maxH; w = h * ratio; }
            doc.addImage(collReportLogoUrl, "PNG", pageW - marginR - w, 4, w, h);
          } catch (e) {}
        }

        // ── White meta band with bold labels ────────────────────────────
        doc.setFillColor(255, 255, 255);
        doc.rect(0, tealBandH, pageW, headerH - tealBandH, "F");
        doc.setDrawColor(200, 220, 240);
        doc.setLineWidth(0.2);
        doc.line(0, headerH, pageW, headerH);

        doc.setFontSize(7.5);
        doc.setTextColor(18, 83, 134);

        // "Generated:" bold + value normal
        doc.setFont("helvetica", "bold");
        doc.text("Generated:", marginL, tealBandH + 6);
        const genLW = doc.getTextWidth("Generated:");
        doc.setFont("helvetica", "normal");
        doc.text(` ${generatedDT}`, marginL + genLW, tealBandH + 6);

        // "Date Range:" bold + value normal (right-aligned block)
        const drLabel = "Date Range:";
        const drValue = `  ${fmtDate(pdfFromDate)} to ${fmtDate(pdfToDate)}`;
        doc.setFont("helvetica", "bold");
        const drLW = doc.getTextWidth(drLabel);
        doc.setFont("helvetica", "normal");
        const drVW = doc.getTextWidth(drValue);
        const drX = pageW - marginR - drLW - drVW;
        doc.setFont("helvetica", "bold");
        doc.text(drLabel, drX, tealBandH + 6);
        doc.setFont("helvetica", "normal");
        doc.text(drValue, drX + drLW, tealBandH + 6);

        doc.setTextColor(0);
      };

      const tableHeadStyles = {
        fillColor: [18, 83, 134],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7,
        cellPadding: 2.5,
        halign: "center",
        valign: "middle",
      };
      const tableBodyStyles = {
        fontSize: 7,
        textColor: [55, 65, 81],
        cellPadding: 2.5,
        valign: "middle",
      };
      const tableAltRowStyles = { fillColor: [240, 247, 255] };
      const tableMargin = { left: marginL, right: marginR, top: tableTopMargin };
      const onNewPage = (data) => {
        if (data.pageNumber > 1) drawPageHeader();
      };

      drawPageHeader();
      let y = tableTopMargin;

      // ── Summary cards row (at top, before sections) ──────────────────
      doc.setTextColor(18, 83, 134);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Summary", marginL, y);
      // Dotted rule after heading
      const sumTW = doc.getTextWidth("Summary");
      doc.setDrawColor(180, 215, 240);
      doc.setLineWidth(0.4);
      try { doc.setLineDash([1, 2]); } catch (e) {}
      doc.line(marginL + sumTW + 3, y - 1.5, pageW - marginR, y - 1.5);
      try { doc.setLineDash([]); } catch (e) {}
      y += 4;

      doc.autoTable({
        startY: y,
        head: [[
          "Total Collected\nAmount",
          "Total Refunded\nAmount",
          "Net Collection\nAmount",
          "Total Invoice\nCount",
          "Total Refund\nCount",
        ]],
        body: [[
          fmtCur(summary?.total_collected),
          fmtCur(summary?.total_refunded),
          fmtCur(summary?.net_collection),
          String(summary?.invoice_count ?? "—"),
          String(summary?.refund_count ?? "—"),
        ]],
        theme: "grid",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [18, 83, 134],
          fontStyle: "bold",
          fontSize: 7.5,
          halign: "center",
          valign: "middle",
          cellPadding: 3,
          lineColor: [180, 215, 240],
          lineWidth: 0.3,
          minCellHeight: 10,
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [33, 205, 192],
          fontStyle: "bold",
          fontSize: 11,
          halign: "center",
          valign: "middle",
          cellPadding: 4,
          lineColor: [180, 215, 240],
          lineWidth: 0.3,
        },
        // Count columns (index 3, 4) use navy instead of teal
        willDrawCell: (data) => {
          if (data.section === "body" && data.column.index >= 3) {
            data.cell.styles.textColor = [18, 83, 134];
          }
        },
        margin: { left: marginL, right: marginR },
        didDrawPage: onNewPage,
      });

      y = doc.lastAutoTable.finalY + 8;

      // ── Collection by Payment Mode ───────────────────────────────────
      const BLOCKED_MODES = new Set(["FAILED", "CANCELLED", "VOID", "REJECTED", "REVERSED"]);
      const MODE_KEYS   = ["UPI", "CASH", "CREDIT CARD", "DEBIT CARD", "BANK TRANSFER"];
      const MODE_LABELS = ["UPI", "Cash", "Credit Card", "Debit Card", "Bank Transfer"];
      const modeCollection = Object.fromEntries(MODE_KEYS.map(k => [k, 0]));
      invoices.forEach((inv) => {
        (inv.payment_details || []).forEach((p) => {
          if (String(p.payment_type || "").toUpperCase() === "REFUND") return;
          if (BLOCKED_MODES.has(String(p.status || "").toUpperCase())) return;
          const mode = String(p.mode_of_payment || "").toUpperCase();
          if (Object.prototype.hasOwnProperty.call(modeCollection, mode)) {
            modeCollection[mode] += Number(p.payment_amount || 0);
          }
        });
      });

      doc.setTextColor(18, 83, 134);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Collection by Payment Mode", marginL, y);
      const modeTW = doc.getTextWidth("Collection by Payment Mode");
      doc.setDrawColor(180, 215, 240);
      doc.setLineWidth(0.4);
      try { doc.setLineDash([1, 2]); } catch (e) {}
      doc.line(marginL + modeTW + 3, y - 1.5, pageW - marginR, y - 1.5);
      try { doc.setLineDash([]); } catch (e) {}
      y += 4;

      doc.autoTable({
        startY: y,
        head: [MODE_LABELS],
        body: [MODE_KEYS.map(k => fmtCur(modeCollection[k] || 0))],
        theme: "grid",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [18, 83, 134],
          fontStyle: "bold",
          fontSize: 7.5,
          halign: "center",
          valign: "middle",
          cellPadding: 3,
          lineColor: [180, 215, 240],
          lineWidth: 0.3,
          minCellHeight: 8,
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [33, 205, 192],
          fontStyle: "bold",
          fontSize: 10,
          halign: "center",
          valign: "middle",
          cellPadding: 3,
          lineColor: [180, 215, 240],
          lineWidth: 0.3,
        },
        margin: { left: marginL, right: marginR },
        didDrawPage: onNewPage,
      });

      y = doc.lastAutoTable.finalY + 10;

      // ── Cash Denomination Summary (only when the facility recorded any) ──
      const cashDenominationSummary = summary?.cash_denomination_summary || [];
      if (summary?.cash_denomination_enabled === true || cashDenominationSummary.length > 0) {
        if (y > pageH - footerH - 60) { doc.addPage(); drawPageHeader(); y = tableTopMargin; }
        doc.setTextColor(18, 83, 134);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Cash Denomination Summary (Net of refunds)", marginL, y);
        const denomTW = doc.getTextWidth("Cash Denomination Summary (Net of refunds)");
        doc.setDrawColor(180, 215, 240);
        doc.setLineWidth(0.4);
        try { doc.setLineDash([1, 2]); } catch (e) {}
        doc.line(marginL + denomTW + 3, y - 1.5, pageW - marginR, y - 1.5);
        try { doc.setLineDash([]); } catch (e) {}
        y += 4;

        doc.autoTable({
          startY: y,
          head: [["Type", "Denomination", "Quantity", "Amount\n(Rs.)"]],
          body: [
            ...(cashDenominationSummary.length
              ? cashDenominationSummary.map((d) => [
                  d.denomination_type === "COIN" ? "Coin" : "Note",
                  `Rs. ${d.denomination}`,
                  String(d.quantity),
                  fmtCur(Number(d.amount || 0)),
                ])
              : [[{ content: "No cash denomination recorded for this period", colSpan: 4, styles: { halign: "center" } }]]),
            [
              { content: "Total Cash", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
              { content: fmtCur(Number(summary?.cash_denomination_total || 0)), styles: { fontStyle: "bold" } },
            ],
            // Same backend totals the dashboard shows, so both explain any gap the same way.
            ...(Math.abs(Number(summary?.cash_unrecorded_total || 0)) > 0.001
              ? [[
                  { content: "Cash with no denomination recorded (not in total)", colSpan: 3, styles: { halign: "right" } },
                  fmtCur(Number(summary.cash_unrecorded_total)),
                ]]
              : []),
            ...(Math.abs(Number(summary?.cash_round_off_total || 0)) > 0.001
              ? [[
                  { content: "Round-off included (paise to nearest rupee)", colSpan: 3, styles: { halign: "right" } },
                  fmtCur(Number(summary.cash_round_off_total)),
                ]]
              : []),
          ],
          headStyles: tableHeadStyles,
          bodyStyles: tableBodyStyles,
          alternateRowStyles: tableAltRowStyles,
          columnStyles: {
            0: { halign: "center" },
            1: { halign: "center" },
            2: { halign: "center" },
            3: { halign: "right" },
          },
          margin: tableMargin,
          didDrawPage: onNewPage,
        });

        y = doc.lastAutoTable.finalY + 10;
      }

      // ── Section 1: Payment List ──────────────────────────────────────
      if (y > pageH - footerH - 25) { doc.addPage(); drawPageHeader(); y = tableTopMargin; }
      doc.setTextColor(18, 83, 134);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("Section 1: Payment List", marginL, y);
      y += 5;

      const sortedInvoices = [...invoices].sort((a, b) =>
        (a.payment_details?.[0]?.created_at || "").localeCompare(
          b.payment_details?.[0]?.created_at || ""
        )
      );
      // One row per receipt — each payment detail is its own line item
      const paymentRows = [];
      sortedInvoices.forEach((inv) => {
        const pmts = (inv.payment_details || []).filter(
          (p) => String(p.payment_type || "").toUpperCase() !== "REFUND"
        );
        pmts.forEach((p) => {
          // Cash payments carry their note/coin breakdown under the mode of payment.
          const denomLines = (p.cash_denominations || [])
            .map((d) => `Rs.${d.denomination}${d.denomination_type === "COIN" ? " coin" : ""} x ${d.quantity}`)
            .join("\n");
          paymentRows.push([
            "",
            inv.invoice_number || "—",
            inv.patient_name || "—",
            inv.invoice_date || "—",
            fmtCur(Number(p.payment_amount || 0)),
            p.receipt_number || "—",
            (p.mode_of_payment || "—").toUpperCase() + (denomLines ? `\n${denomLines}` : ""),
            fmtDateTime(p.created_at),
          ]);
        });
      });
      paymentRows.forEach((row, i) => { row[0] = String(i + 1); });

      doc.autoTable({
        startY: y,
        head: [["Sr.\nNo.", "Invoice Number", "Patient Name", "Invoice\nDate", "Amount\n(Rs.)", "Receipt Number(s)", "Mode of\nPayment", "Created At"]],
        body: paymentRows.length
          ? paymentRows
          : [["", "No records found", "", "", "", "", "", ""]],
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        alternateRowStyles: tableAltRowStyles,
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 30 },
          2: { cellWidth: 24 },
          3: { cellWidth: 20, halign: "center" },
          4: { cellWidth: 20, halign: "right" },
          5: { cellWidth: 32 },
          6: { cellWidth: 22, halign: "center" },
          7: { cellWidth: 24, halign: "center" },
        },
        margin: tableMargin,
        didDrawPage: onNewPage,
      });

      y = doc.lastAutoTable.finalY + 10;

      // ── Dotted separator between sections ───────────────────────────
      if (y > pageH - footerH - 25) {
        doc.addPage(); drawPageHeader(); y = tableTopMargin;
      } else {
        doc.setDrawColor(200, 220, 240);
        doc.setLineWidth(0.4);
        try { doc.setLineDash([1, 2]); } catch (e) {}
        doc.line(marginL, y - 5, pageW - marginR, y - 5);
        try { doc.setLineDash([]); } catch (e) {}
      }

      // ── Section 2: Refund List ───────────────────────────────────────
      doc.setTextColor(18, 83, 134);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("Section 2: Refund List", marginL, y);
      y += 5;

      const sortedRefunds = [...refunds].sort((a, b) =>
        (a.refund_payment_details?.[0]?.created_at || "").localeCompare(
          b.refund_payment_details?.[0]?.created_at || ""
        )
      );
      const refundRows = sortedRefunds.map((ref, idx) => {
        const payout = (ref.refund_payment_details || [])[0] || {};
        // Cash refunds carry the notes/coins handed back under the mode of payment.
        const denomLines = (payout.cash_denominations || [])
          .map((d) => `Rs.${d.denomination}${d.denomination_type === "COIN" ? " coin" : ""} x ${d.quantity}`)
          .join("\n");
        return [
          String(idx + 1),
          ref.invoice_number || "—",
          ref.refund_number || "—",
          ref.patient_name || "—",
          fmtCur(ref.approved_amount),
          (payout.mode_of_payment || "—").toUpperCase() + (denomLines ? `\n${denomLines}` : ""),
          fmtDateTime(payout.created_at),
        ];
      });

      doc.autoTable({
        startY: y,
        head: [["Sr.\nNo.", "Invoice Number", "Refund Number", "Patient Name", "Approved Amount\n(Rs.)", "Mode of\nPayment", "Created At"]],
        body: refundRows.length
          ? refundRows
          : [["", "No records found", "", "", "", "", ""]],
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        alternateRowStyles: tableAltRowStyles,
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 32 },
          2: { cellWidth: 30 },
          3: { cellWidth: 28 },
          4: { cellWidth: 30, halign: "right" },
          5: { cellWidth: 24, halign: "center" },
          6: { cellWidth: 24, halign: "center" },
        },
        margin: tableMargin,
        didDrawPage: onNewPage,
      });

      y = doc.lastAutoTable.finalY + 8;

      // ── Footer (line + 3-part text) on every page ────────────────────
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(marginL, pageH - 10, pageW - marginR, pageH - 10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text(`Generated from ${facilityDisplayName}`, marginL, pageH - 6);
        doc.text(
          `Thank you for your trust in ${facilityDisplayName}.`,
          pageW / 2, pageH - 6, { align: "center" }
        );
        doc.text(`Page ${i} of ${totalPages}`, pageW - marginR, pageH - 6, { align: "right" });
      }

      doc.save(`Collection_Report_${pdfFromDate}_to_${pdfToDate}.pdf`);
      toast.success("Collection Report downloaded successfully.");
    } catch (err) {
      console.error("Collection report PDF error:", err);
      toast.error("Failed to generate collection report PDF.");
    } finally {
      setCollectionReportLoading(false);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    if (!authToken) return;
    setDashboardLoading(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_all_invoices/`,
        { hip_id: facilityName, is_pagination_required: false },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const invoices = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
      setDashboardInvoices(invoices);
    } catch (e) {
      console.error("Dashboard data fetch error:", e);
    } finally {
      setDashboardLoading(false);
    }
  }, [authToken, facilityName, REACT_APP_BACKEND_ROUTE_URL]);

  useEffect(() => {
    if (authToken) fetchDashboardData();
  }, [authToken, fetchDashboardData]);

  const dashboardStats = useMemo(() => {
    const loggedInUserId = getCurrentUserId();

    const isBlockedStatus = (status) => {
      const s = String(status || "").toUpperCase();
      return ["FAILED", "CANCELLED", "VOID", "REJECTED", "REVERSED"].includes(s);
    };

    const isUserInvoice = (inv) => {
      if (loggedInUserId == null) return true;
      if (inv.created_by != null && Number(inv.created_by) === loggedInUserId) return true;
      if (Array.isArray(inv.payment_details)) {
        return inv.payment_details.some((p) => Number(p.created_by) === loggedInUserId);
      }
      return false;
    };

    const calcPaidAmt = (inv) => {
      let paid = Number(inv?.partial_payment ?? inv?.amount_paid ?? 0);
      if (Array.isArray(inv?.payment_details) && inv.payment_details.length > 0) {
        const sum = inv.payment_details
          .filter((p) => {
            const pt = String(p?.payment_type || "").toUpperCase();
            if (pt === "REFUND") return false;
            return !isBlockedStatus(p?.status);
          })
          .reduce((acc, p) => acc + Number(p?.payment_amount ?? 0), 0);
        if (sum > paid) paid = sum;
      }
      return paid;
    };

    const isPaymentByUser = (p) => {
      if (isBillingAdmin || loggedInUserId == null) return true;
      return Number(p.created_by) === loggedInUserId;
    };

    const userInvoices = dashboardInvoices.filter(isUserInvoice);
    const dateFilteredInvoices = userInvoices.filter(
      (inv) => (inv.invoice_date || "").startsWith(dashboardDate)
    );
    const collectionInvoices = isBillingAdmin ? dashboardInvoices : userInvoices;

    let todayCollection = 0;
    collectionInvoices.forEach((inv) => {
      (inv.payment_details || []).forEach((p) => {
        const pDate = (p.created_at || "").split("T")[0];
        if (pDate !== dashboardDate) return;
        if (!isPaymentByUser(p)) return;
        const pt = String(p.payment_type || "").toUpperCase();
        if (pt === "REFUND") return;
        if (isBlockedStatus(p.status)) return;
        todayCollection += Number(p.payment_amount || 0);
      });
    });

    const totalInvoices = dateFilteredInvoices.length;

    const partiallyPaidCount = dateFilteredInvoices.filter((inv) => {
      const total = Number(inv.total_amount || 0);
      const paid = calcPaidAmt(inv);
      return paid >= 1 && paid < total - 0.01;
    }).length;

    let pendingAmount = 0;
    dateFilteredInvoices.forEach((inv) => {
      const total = Number(inv.total_amount || 0);
      const paid = calcPaidAmt(inv);
      const outstanding = total - paid;
      if (outstanding > 0.001) pendingAmount += outstanding;
    });

    let refundAmount = 0;
    collectionInvoices.forEach((inv) => {
      (inv.payment_details || []).forEach((p) => {
        const pDate = (p.created_at || "").split("T")[0];
        if (pDate !== dashboardDate) return;
        if (!isPaymentByUser(p)) return;
        const pt = String(p.payment_type || "").toUpperCase();
        if (pt === "REFUND") refundAmount += Number(p.payment_amount || 0);
      });
    });

    const MODES = ["UPI", "CASH", "CREDIT CARD", "DEBIT CARD", "BANK TRANSFER"];
    const modeCollection = Object.fromEntries(MODES.map((m) => [m, 0]));
    collectionInvoices.forEach((inv) => {
      (inv.payment_details || []).forEach((p) => {
        const pDate = (p.created_at || "").split("T")[0];
        if (pDate !== dashboardDate) return;
        if (!isPaymentByUser(p)) return;
        const pt = String(p.payment_type || "").toUpperCase();
        if (pt === "REFUND") return;
        if (isBlockedStatus(p.status)) return;
        const mode = String(p.mode_of_payment || "").toUpperCase().trim();
        if (Object.prototype.hasOwnProperty.call(modeCollection, mode)) {
          modeCollection[mode] += Number(p.payment_amount || 0);
        }
      });
    });

    return { todayCollection, totalInvoices, partiallyPaidCount, pendingAmount, refundAmount, modeCollection };
  }, [dashboardInvoices, dashboardDate, isBillingAdmin]);

  // Auto-apply collection report on initial load using today's date
  useEffect(() => {
    if (!authToken) return;
    const today = getLocalDateStr();
    const userId = getCurrentUserId();
    if (!userId) return;
    setCollectionDashboardLoading(true);
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}collection_report/`,
        { hip_id: facilityName, user_id: userId, from_date: today, to_date: today },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data?.status === "Success") setCollectionReportData(res.data);
      })
      .catch((err) => console.error("Auto collection report error:", err))
      .finally(() => setCollectionDashboardLoading(false));
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const collectionDashboardStats = useMemo(() => {
    if (!collectionReportData) return null;
    const { summary, invoices = [] } = collectionReportData;

    const BLOCKED = new Set(["FAILED", "CANCELLED", "VOID", "REJECTED", "REVERSED"]);
    const isBlocked = (s) => BLOCKED.has(String(s || "").toUpperCase());

    const calcPaid = (inv) => {
      let paid = Number(inv?.partial_payment ?? inv?.amount_paid ?? 0);
      if (Array.isArray(inv?.payment_details)) {
        const sum = inv.payment_details
          .filter((p) => String(p?.payment_type || "").toUpperCase() !== "REFUND" && !isBlocked(p?.status))
          .reduce((acc, p) => acc + Number(p?.payment_amount ?? 0), 0);
        if (sum > paid) paid = sum;
      }
      return paid;
    };

    const partiallyPaid = invoices.filter((inv) => {
      const total = Number(inv.total_amount || 0);
      const paid = calcPaid(inv);
      return paid >= 1 && paid < total - 0.01;
    }).length;

    const pendingAmount = invoices.reduce((sum, inv) => {
      const diff = Number(inv.total_amount || 0) - calcPaid(inv);
      return diff > 0.001 ? sum + diff : sum;
    }, 0);

    const MODES = ["UPI", "CASH", "CREDIT CARD", "DEBIT CARD", "BANK TRANSFER"];
    const modeCollection = Object.fromEntries(MODES.map((m) => [m, 0]));
    invoices.forEach((inv) => {
      (inv.payment_details || []).forEach((p) => {
        if (String(p.payment_type || "").toUpperCase() === "REFUND") return;
        if (isBlocked(p.status)) return;
        const mode = String(p.mode_of_payment || "").toUpperCase().trim();
        if (Object.prototype.hasOwnProperty.call(modeCollection, mode)) {
          modeCollection[mode] += Number(p.payment_amount || 0);
        }
      });
    });

    return {
      totalCollected: Number(summary?.total_collected || 0),
      totalRefunded: Number(summary?.total_refunded || 0),
      netCollection: Number(summary?.net_collection || 0),
      invoiceCount: Number(summary?.invoice_count || 0),
      refundCount: Number(summary?.refund_count || 0),
      partiallyPaid,
      pendingAmount,
      modeCollection,
      cashDenominationEnabled: summary?.cash_denomination_enabled === true,
      cashDenominationSummary: summary?.cash_denomination_summary || [],
      cashDenominationTotal: Number(summary?.cash_denomination_total || 0),
      // From the same payments as the denomination summary, so the two always reconcile.
      cashUnrecordedTotal: Number(summary?.cash_unrecorded_total || 0),
      cashRoundOffTotal: Number(summary?.cash_round_off_total || 0),
    };
  }, [collectionReportData]);


  return (
    <>
      {/* Open Staging Area Button */}
      <Tooltip title="Open Stageing Area">
        <Fab
          style={{
            color: "#fff",
            backgroundColor: "#0E3F66",
            overflow: "hidden",
            position: "fixed",
            bottom: 86,
            right: 16,
            marginRight: 1,
            borderRadius: "30px",
          }}
          color="primary"
          onClick={handleDrawerOpen}
        >
          <ArrowForwardIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: { width: "40%" },
        }}
      >
        <div style={{ padding: "20px" }}>
          <Button onClick={handleDrawerClose} style={{ marginBottom: "10px" }}>
            <ArrowBackIcon />
          </Button>
          {drawerOpen && <StagingArea pathname="/newInvoice" />}
        </div>
      </Drawer>

      <SideNavbar />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          padding: "12px",
          gap: "20px", // Space between text and button
          marginBottom: "5px",
          // paddingX: { xs: 2, sm: 3 }, // Add padding for smaller screens
        }}
      >
        <p
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            textAlign: "left",
            color: "#172935",
            margin: 0, // Remove default margin to align properly
          }}
        >
          Billing Invoices
        </p>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title="Select Patient">
            <IconButton
              onClick={() => setOpenPatientSearch(true)}
              sx={{
                border: "1px solid #0E3F66",
                borderRadius: "10px",
                color: "#0E3F66",
                width: "42px",
                height: "42px",
              }}
            >
              <PersonSearchIcon />
            </IconButton>
          </Tooltip>
          {selectedPatientFilter?.patient_id && (
            <Tooltip title="Clear Patient Selection">
              <IconButton
                onClick={() => setSelectedPatientFilter(null)}
                sx={{
                  border: "1px solid #0E3F66",
                  borderRadius: "10px",
                  color: "#0E3F66",
                  width: "42px",
                  height: "42px",
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
          <TextField
            size="small"
            placeholder="Search by Name"
            value={invoiceSearchText}
            onChange={(e) => {
              if (selectedPatientFilter?.patient_id) {
                setSelectedPatientFilter(null);
              }
              setInvoiceSearchText(e.target.value);
            }}
            className="billing-search-field"
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                height: "42px",
                borderRadius: "10px",
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <button
            onClick={handleAddItem}
            type="button"
            className="btn btn__secondary btn__rounded btn__block billing-header-action-btn"
            // variant="contained"
            // color="primary"
            style={{
              backgroundColor: "#0E3F66",
              borderRadius: "10px",
              fontSize: "14px",
              paddingX: { xs: 2, sm: 3 },
              paddingY: 1,
              textTransform: "none",
              height: "42px",
              // "&:hover": {
              //   backgroundColor: "#0C3555",
              // },
            }}
          >
            + Add Invoice
          </button>
          <button
            onClick={handleCreateEstimate}
            type="button"
            className="btn btn__secondary btn__rounded btn__block billing-header-action-btn"
            style={{
              backgroundColor: "#0E3F66",
              borderRadius: "10px",
              fontSize: "14px",
              paddingX: { xs: 2, sm: 3 },
              paddingY: 1,
              textTransform: "none",
              height: "42px",
            }}
          >
            + Add Estimate
          </button>
        </Box>

      </Box>

      {/* ===== Collection Dashboard ===== */}
      <Box sx={{ px: "12px", mb: 2 }}>
        <Accordion
          expanded={collectionDashboardOpen}
          onChange={(_, isExpanded) => setCollectionDashboardOpen(isExpanded)}
          disableGutters
          sx={{
            backgroundColor: "#F4F4F4",
            boxShadow: "none",
            borderRadius: "6px !important",
            border: "1px solid #B1B1B1BB",
            "&:before": { display: "none" },
            "&.Mui-expanded": {
              backgroundColor: "#E9FAF9",
              border: "1px solid #4DD7CD",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#2a3c50" }} />}
            sx={{
              minHeight: "42px",
              px: "12px",
              "& .MuiAccordionSummary-content": { margin: 0, alignItems: "center" },
              "&.Mui-expanded": { backgroundColor: "#E9FAF9" },
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#2a3c50" }}>
              Collection Dashboard
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ px: "16px", py: "12px", backgroundColor: "#E9FAF9", borderTop: "1px solid #e0e0e0" }}
          >
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
              Collection Report:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>From</Typography>
              <DatePicker
                selected={collectionReportFromDate ? new Date(collectionReportFromDate + "T00:00:00") : null}
                onChange={(d) => setCollectionReportFromDate(d ? getLocalDateStr(d) : "")}
                selectsStart
                startDate={collectionReportFromDate ? new Date(collectionReportFromDate + "T00:00:00") : null}
                endDate={collectionReportToDate ? new Date(collectionReportToDate + "T00:00:00") : null}
                maxDate={collectionReportToDate ? new Date(collectionReportToDate + "T00:00:00") : undefined}
                dateFormat="dd-MM-yyyy"
                customInput={<CalendarTrigger placeholder="From date" />}
                popperPlacement="bottom-start"
              />
              <Typography sx={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>To</Typography>
              <DatePicker
                selected={collectionReportToDate ? new Date(collectionReportToDate + "T00:00:00") : null}
                onChange={(d) => setCollectionReportToDate(d ? getLocalDateStr(d) : "")}
                selectsEnd
                startDate={collectionReportFromDate ? new Date(collectionReportFromDate + "T00:00:00") : null}
                endDate={collectionReportToDate ? new Date(collectionReportToDate + "T00:00:00") : null}
                minDate={collectionReportFromDate ? new Date(collectionReportFromDate + "T00:00:00") : undefined}
                dateFormat="dd-MM-yyyy"
                customInput={<CalendarTrigger placeholder="To date" />}
                popperPlacement="bottom-start"
              />
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={applyCollectionReport}
              disabled={collectionDashboardLoading}
              sx={{
                bgcolor: "#125386",
                color: "white",
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                height: "32px",
                "&:hover": { bgcolor: "#0e3f66" },
                "&.Mui-disabled": { bgcolor: "#9ca3af", color: "white" },
              }}
            >
              {collectionDashboardLoading ? "Applying..." : "Apply"}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={downloadCollectionReportPdf}
              disabled={collectionReportLoading || !collectionReportData}
              startIcon={<SaveAlt />}
              sx={{
                bgcolor: "#0E3F66",
                color: "white",
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                height: "32px",
                "&:hover": { bgcolor: "#0C3555" },
                "&.Mui-disabled": { bgcolor: "#9ca3af", color: "white" },
              }}
            >
              {collectionReportLoading ? "Generating..." : "Download Collection Report"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {[
            {
              label: "Collection Amount",
              value: collectionDashboardStats ? `₹${formatInr(collectionDashboardStats.totalCollected)}` : "—",
              color: "#065f46",
              bg: "#d1fae5",
              border: "#6ee7b7",
            },
            {
              label: "Total Invoices",
              value: collectionDashboardStats ? collectionDashboardStats.invoiceCount : "—",
              color: "#1e3a5f",
              bg: "#dbeafe",
              border: "#93c5fd",
            },
            {
              label: "Partially Paid",
              value: collectionDashboardStats ? collectionDashboardStats.partiallyPaid : "—",
              color: "#9a3412",
              bg: "#ffedd5",
              border: "#fdba74",
            },
            {
              label: "Pending Amount",
              value: collectionDashboardStats ? `₹${formatInr(collectionDashboardStats.pendingAmount)}` : "—",
              color: "#991b1b",
              bg: "#fee2e2",
              border: "#fca5a5",
            },
            {
              label: "Refund Amount",
              value: collectionDashboardStats ? `₹${formatInr(collectionDashboardStats.totalRefunded)}` : "—",
              color: "#5b21b6",
              bg: "#ede9fe",
              border: "#c4b5fd",
            },
          ].map((card) => (
            <Box
              key={card.label}
              sx={{
                flex: "1 1 150px",
                border: `1px solid ${card.border}`,
                borderRadius: "10px",
                backgroundColor: card.bg,
                p: "12px 16px",
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: "11px",
                }}
              >
                {card.label}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: card.color }}>
                {collectionDashboardLoading ? "..." : card.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Payment Mode Breakdown */}
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "11px",
              color: "text.secondary",
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}
          >
            Collection by Payment Mode
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[
              { mode: "UPI",           label: "UPI",           color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
              { mode: "CASH",          label: "Cash",          color: "#065f46", bg: "#ecfdf5", border: "#6ee7b7" },
              { mode: "CREDIT CARD",   label: "Credit Card",   color: "#6b21a8", bg: "#faf5ff", border: "#d8b4fe" },
              { mode: "DEBIT CARD",    label: "Debit Card",    color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
              { mode: "BANK TRANSFER", label: "Bank Transfer", color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
            ].map((item) => (
              <Box
                key={item.mode}
                sx={{
                  flex: "1 1 130px",
                  border: `1px solid ${item.border}`,
                  borderRadius: "10px",
                  backgroundColor: item.bg,
                  p: "10px 14px",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    fontWeight: 500,
                    color: "text.secondary",
                    fontSize: "11px",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "16px", color: item.color }}>
                  {collectionDashboardLoading
                    ? "..."
                    : collectionDashboardStats
                      ? `₹${formatInr(collectionDashboardStats.modeCollection?.[item.mode] || 0)}`
                      : "—"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Cash Denomination Breakdown — for facilities with the feature enabled */}
        {(collectionDashboardStats?.cashDenominationEnabled ||
          collectionDashboardStats?.cashDenominationSummary?.length > 0) && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "11px",
                color: "text.secondary",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              Cash Denomination (Net of refunds, Total ₹{formatInr(collectionDashboardStats.cashDenominationTotal)})
            </Typography>
            {collectionDashboardStats.cashDenominationSummary.length === 0 && (
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                No cash denomination recorded for this period.
              </Typography>
            )}
            {Math.abs(collectionDashboardStats.cashUnrecordedTotal) > 0.001 && (
              <Typography sx={{ fontSize: "12px", color: "#c2410c", mb: 1 }}>
                ₹{formatInr(collectionDashboardStats.cashUnrecordedTotal)} of the net cash has no denomination recorded.
              </Typography>
            )}
            {Math.abs(collectionDashboardStats.cashRoundOffTotal) > 0.001 && (
              <Typography sx={{ fontSize: "12px", color: "text.secondary", mb: 1 }}>
                Includes ₹{formatInr(collectionDashboardStats.cashRoundOffTotal)} round-off (bills with paise collected to the nearest rupee).
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {collectionDashboardStats.cashDenominationSummary.map((d) => (
                <Box
                  key={`${d.denomination_type}-${d.denomination}`}
                  sx={{
                    flex: "1 1 130px",
                    border: "1px solid #6ee7b7",
                    borderRadius: "10px",
                    backgroundColor: "#ecfdf5",
                    p: "8px 12px",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 500, color: "text.secondary", fontSize: "11px" }}
                  >
                    {d.denomination_type === "COIN" ? "Coin" : "Note"} ₹{d.denomination} × {d.quantity}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#065f46" }}>
                    ₹{formatInr(Number(d.amount || 0))}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
          </AccordionDetails>
        </Accordion>
      </Box>
      {/* ===== End Collection Dashboard ===== */}

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <Box sx={{ mx: "12px", mb: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {(activeFilters.fromDate || activeFilters.toDate) && (
            <FilterChip
              label={`Invoice Date: ${activeFilters.fromDate || "…"} → ${activeFilters.toDate || "…"}`}
              onClear={() => clearFilterKey(["fromDate", "toDate"])}
            />
          )}
          {activeFilters.invoiceNumber && (
            <FilterChip label={`Invoice No: ${activeFilters.invoiceNumber}`} onClear={() => clearFilterKey(["invoiceNumber"])} />
          )}
          {activeFilters.patientName && (
            <FilterChip label={`Patient: ${activeFilters.patientName}`} onClear={() => clearFilterKey(["patientName"])} />
          )}
          {(activeFilters.dueDateFrom || activeFilters.dueDateTo) && (
            <FilterChip
              label={`Due Date: ${activeFilters.dueDateFrom || "…"} → ${activeFilters.dueDateTo || "…"}`}
              onClear={() => clearFilterKey(["dueDateFrom", "dueDateTo"])}
            />
          )}
          {(activeFilters.minAmount || activeFilters.maxAmount) && (
            <FilterChip
              label={`Amount: ₹${activeFilters.minAmount || "0"} – ₹${activeFilters.maxAmount || "∞"}`}
              onClear={() => clearFilterKey(["minAmount", "maxAmount"])}
            />
          )}
          {(activeFilters.minPaid || activeFilters.maxPaid) && (
            <FilterChip
              label={`Paid: ₹${activeFilters.minPaid || "0"} – ₹${activeFilters.maxPaid || "∞"}`}
              onClear={() => clearFilterKey(["minPaid", "maxPaid"])}
            />
          )}
          {(activeFilters.minOutstanding || activeFilters.maxOutstanding) && (
            <FilterChip
              label={`Outstanding: ₹${activeFilters.minOutstanding || "0"} – ₹${activeFilters.maxOutstanding || "∞"}`}
              onClear={() => clearFilterKey(["minOutstanding", "maxOutstanding"])}
            />
          )}
          {activeFilters.paymentStatus && (
            <FilterChip label={`Status: ${activeFilters.paymentStatus}`} onClear={() => clearFilterKey(["paymentStatus"])} />
          )}
          <Box
            sx={{ fontSize: "12px", color: "#ef4444", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
            onClick={() => applyFilters(EMPTY_FILTERS)}
          >
            Clear All
          </Box>
        </Box>
      )}

      <div className="display-flex">
        {filteredInvoiceList !== undefined && (filteredInvoiceList.length > 0 || invoiceList.length > 0) ? (
          <DataGrid
            rows={filteredInvoiceList}
            columns={[
              {
                field: "invoice_number",
                headerName: "Invoice Number",
                width: 180,
              },
              {
                field: "patient_name",
                headerName: "Patient Name",
                width: 180,
                renderCell: (params) => (
                  <span>{displayPatientName(params.value)}</span>
                ),
              },
              {
                field: "invoice_date",
                headerName: "Invoice Date",
                width: 150,
                renderCell: (params) => <DisplayDate value={params.value} emptyLabel="" />,
              },
              {
                field: "due_date",
                headerName: "Due Date",
                width: 150,
                renderCell: (params) => <DisplayDate value={params.value} emptyLabel="" />,
              },
              { field: "total_amount", headerName: "Gross Amount", width: 120 },
              {
                field: "paid_amount",
                headerName: "Paid Amount",
                width: 130,
                align: "right",
                headerAlign: "right",
                sortable: true,
                valueGetter: (value, row) => getInvoiceCollectedAmount(row),
                renderCell: (params) => (
                  <span>{formatInr(getInvoiceCollectedAmount(params.row))}</span>
                ),
              },
              {
                field: "refunded_amount",
                headerName: "Refunded",
                width: 120,
                align: "right",
                headerAlign: "right",
                sortable: true,
                valueGetter: (value, row) => getInvoiceRefundedAmount(row),
                renderCell: (params) => {
                  const refunded = getInvoiceRefundedAmount(params.row);
                  return (
                    <span style={refunded > 0.001 ? { color: "#9a3412", fontWeight: 500 } : undefined}>
                      {formatInr(refunded)}
                    </span>
                  );
                },
              },
              {
                field: "outstanding",
                headerName: "Outstanding",
                width: 130,
                align: "right",
                headerAlign: "right",
                sortable: true,
                valueGetter: (value, row) => getInvoiceOutstanding(row),
                renderCell: (params) => (
                  <span>{formatInr(getInvoiceOutstanding(params.row))}</span>
                ),
              },
              {
                field: "payment_status",
                headerName: "Payment Status",
                width: 130,
                renderCell: (params) => {
                  const displayStatus = getPaymentStatus(params.row);
                  let textColor = "#172935"; // default dark

                  if (displayStatus === "Paid") {
                      textColor = "#065f46"; // green
                  } else if (displayStatus === "Partially Paid") {
                      textColor = "#9a3412"; // orange
                  } else {
                      textColor = "#991b1b"; // red
                  }

                  return (
                    <span style={{ color: textColor, fontWeight: 500 }}>
                      {displayStatus}
                    </span>
                  );
                },
              },
              {
                field: "make_payment",
                headerName: "Make Payment",
                width: 150,
                sortable: false,
                renderCell: (params) => {
                  const canPay = getInvoiceOutstanding(params.row) > 0.001;
                  
                  return canPay ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handlePayNow(params.row)}
                      style={{
                        backgroundColor: "#0E3F66",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: "8px",
                      }}
                    >
                      Pay Now
                    </Button>
                  ) : null;
                },
              },
              {
                field: "actions",
                headerName: "Actions",
                headerAlign: "center",
                align: "center",
                width: 200,
                minWidth: 200,
                sortable: false,
                renderCell: (params) => {
                  // Net of refunds on both sides, so a refunded invoice that is
                  // otherwise settled still counts as fully paid here.
                  const total = getInvoiceNetTotal(params.row);
                  const paid = getInvoicePaidAmount(params.row);

                  const isFullyPaid = paid >= total - 0.01 && total > 0;
                  const isPartiallyPaid = paid >= 1 && !isFullyPaid;
                  
                  const actionsEnabled = isFullyPaid || isPartiallyPaid;
                  // Discount edit for any unpaid / partially paid invoice
                  const canEditDiscount = !isFullyPaid;

                  const deleteEnabled = isFullyPaid && isBillingAdmin;
                  const viewEnabled = actionsEnabled;
                  const mailEnabled = actionsEnabled;
                  return (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "nowrap", alignItems: "center", justifyContent: "center", width: "100%" }}>
                      <Tooltip
                        title={
                          canEditDiscount
                            ? "Edit discount"
                            : "Fully paid invoices cannot be edited"
                        }
                      >
                        <EditIcon
                          style={{
                            color: canEditDiscount ? "#0D233F" : "#ccc",
                            fontSize: "18px",
                            cursor: canEditDiscount ? "pointer" : "not-allowed",
                            display: "block",
                            margin: "auto",
                            opacity: canEditDiscount ? 1 : 0.5,
                            pointerEvents: canEditDiscount ? "auto" : "none",
                          }}
                          onClick={() =>
                            canEditDiscount && handleEdit(params.row.invoice_id)
                          }
                        />
                      </Tooltip>
                      {/* <Tooltip title={deleteEnabled ? "Delete" : isFullyPaid ? "Delete (billing admin only)" : "Available when invoice is paid"}>
                        <DeleteIcon
                          style={{
                            color: deleteEnabled ? "#0D233F" : "#ccc",
                            fontSize: "18px",
                            cursor: deleteEnabled ? "pointer" : "not-allowed",
                            display: "block",
                            margin: "auto",
                            opacity: deleteEnabled ? 1 : 0.5,
                            pointerEvents: deleteEnabled ? "auto" : "none",
                          }}
                          onClick={() => deleteEnabled && handleDelete(params.row.invoice_id)}
                        />
                      </Tooltip> */}
                      <Tooltip title={viewEnabled ? "View" : "Available when invoice is paid/partially paid"}>
                        <VisibilityIcon
                          style={{
                            color: viewEnabled ? "#0D233F" : "#ccc",
                            fontSize: "18px",
                            cursor: viewEnabled ? "pointer" : "not-allowed",
                            display: "block",
                            opacity: viewEnabled ? 1 : 0.5,
                            pointerEvents: viewEnabled ? "auto" : "none",
                          }}
                          onClick={() => viewEnabled && handleView(params.row.invoice_id)}
                        />
                      </Tooltip>
                      <Tooltip title={mailEnabled ? "Send Mail" : !isEmailEnabled ? "Email feature is disabled" : "Available when invoice is paid/partially paid"}>
                        <MailIcon
                          style={{
                            color: mailEnabled ? "#0D233F" : "#ccc",
                            fontSize: "18px",
                            cursor: mailEnabled ? "pointer" : "not-allowed",
                            display: "block",
                            opacity: mailEnabled ? 1 : 0.5,
                            pointerEvents: mailEnabled ? "auto" : "none",
                          }}
                          onClick={() => mailEnabled && handleSendMail(params.row.invoice_id)}
                        />
                      </Tooltip>
                      <Tooltip title="Department slips">
                        <PrintIcon
                          style={{
                            color: "#0D233F",
                            fontSize: "18px",
                            cursor: "pointer",
                            display: "block",
                          }}
                          onClick={() => handlePrintDepartmentSlips(params.row.invoice_id)}
                        />
                      </Tooltip>
                    </div>
                  );
                },
              },
            ]}
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Grid item xs={12} sm={6} md={8} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <GridToolbarColumnsButton />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setFilterModalOpen(true)}
                      startIcon={<TuneIcon sx={{ fontSize: "18px !important" }} />}
                      sx={{
                        color: activeFilterCount > 0 ? "#0E3F66" : "inherit",
                        fontWeight: activeFilterCount > 0 ? 700 : 400,
                        textTransform: "none",
                        fontSize: "13px",
                        gap: 0.5,
                      }}
                    >
                      Filters
                      {activeFilterCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            bgcolor: "#0E3F66",
                            color: "white",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.1,
                            lineHeight: 1.6,
                          }}
                        >
                          {activeFilterCount}
                        </Box>
                      )}
                    </Button>
                    <GridToolbarDensitySelector />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{ display: "flex", gap: 1, justifyContent: { xs: "center", sm: "flex-end" } }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => generatePdf()}
                      startIcon={<SaveAlt />}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Download PDF
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={downloadCsv}
                      startIcon={<SaveAlt />}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Download CSV
                    </Button>
                  </Grid>
                </Grid>
              ),
            }}
            paginationMode="server"
            rowCount={invoicePagination.total_records}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => setPaginationModel(model)}
            loading={invoiceLoading}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowClassName={getRowClassName}
            sx={{
              "& .MuiDataGrid-root": {
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                fontFamily: "Arial, sans-serif",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#125386",
                color: "white",
                fontSize: "13px",
                fontWeight: "600",
                minHeight: "48px !important",
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "white",
                  fontWeight: "600",
                  fontSize: "13px",
                },
                "& .MuiDataGrid-columnHeader": {
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  padding: "0 12px",
                },
                "& .MuiDataGrid-columnHeader:last-child": {
                  borderRight: "none",
                },
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "white",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "white",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "white",
                },
                "& .MuiDataGrid-columnHeaderSortable": {
                  "& .MuiDataGrid-sortIcon": {
                    color: "white",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  "& .MuiDataGrid-sortIcon": {
                    color: "white",
                  },
                },
                "& .MuiSvgIcon-root": {
                  color: "white",
                },
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "white",
                minHeight: "40px !important",
                "&:nth-of-type(even)": {
                  backgroundColor: "white",
                },
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
                "&.Mui-selected": {
                  backgroundColor: "#e3f2fd",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                },
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-cell": {
                borderRight: "1px solid #e0e0e0",
                fontSize: "12px",
                color: "#374151",
                padding: "8px 12px",
                minHeight: "40px !important",
                "&:last-child": {
                  borderRight: "none",
                },
                "&:focus": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible",
                "& .MuiDataGrid-cellContent": {
                  overflow: "visible",
                },
              },
              "& .MuiDataGrid-cellContent": {
                fontSize: "12px",
                color: "#374151",
                fontWeight: "400",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#f8f9fa",
                borderTop: "1px solid #e0e0e0",
                minHeight: "52px",
              },
              "& .MuiTablePagination-root": {
                color: "#374151",
                fontSize: "12px",
              },
              "& .MuiDataGrid-toolbarContainer": {
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
                padding: "8px 16px",
              },
            }}
          />
        ) : (
          <NoDataPage />
        )}
      </div>

      <div
        id="invoice-template"
        style={{ position: "absolute", left: "-9999px" }}
      >
        {selectedInvoice ? (
          <Card style={{ height: "auto" }}>
            <Grid
              container
              style={{ backgroundColor: themeColour, padding: "20px" }}
            >
              <Grid item xs={6}>
                <Typography
                  variant="h4"
                  style={{ color: "black", fontWeight: "bold" }}
                >
                  INVOICE
                </Typography>
              </Grid>
              <Grid item xs={6} textAlign="left">
                {collateralImage && (
                  <img
                    src={`data:image/png;base64,${collateralImage}`}
                    alt="Collateral"
                    width="100px"
                    height="60px"
                    style={{ paddingBottom: "3px", paddingTop: "5px" }}
                  />
                )}
              </Grid>
            </Grid>

            <Grid container style={{ padding: "20px", marginTop: "10px" }}>
              <Grid item xs={6}>
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Billed To
                </Typography>
                <Typography variant="subtitle1">
                  {displayPatientName(selectedInvoice.patient_name)}
                </Typography>
                <Typography variant="subtitle1">
                  {selectedInvoice.address}
                </Typography>
                <Typography variant="subtitle1">
                  {selectedInvoice.phone}
                </Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Invoice No: {selectedInvoice.invoice_number}
                </Typography>
                <Typography variant="subtitle1">
                  Date: {selectedInvoice.invoice_date}
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={12} style={{ padding: "20px" }}>
              <Grid
                container
                style={{
                  borderTop: "1px solid #ccc",
                  borderBottom: "1px solid #ccc",
                  padding: "10px",
                  fontWeight: "bold",
                }}
              >
                <Grid item xs={4}>
                  Item
                </Grid>
                <Grid item xs={2}>
                  Unit Cost (₹)
                </Grid>
                <Grid item xs={2}>
                  Qty
                </Grid>
                <Grid item xs={2}>
                  Discount (%)
                </Grid>
                <Grid item xs={2}>
                  Amount (₹)
                </Grid>
              </Grid>

              {selectedInvoice.items.map((item, index) => (
                <Grid
                  container
                  key={index}
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "10px 0",
                  }}
                >
                  <Grid item xs={4}>
                    {item.item_name}
                  </Grid>
                  <Grid item xs={2}>
                    ₹{item.item_price}
                  </Grid>
                  <Grid item xs={2}>
                    {item.quantity}
                  </Grid>
                  <Grid item xs={2}>
                    {Number(item?.discount_percentage || 0).toFixed(2)}
                  </Grid>
                  <Grid item xs={2}>
                    ₹{item.subtotal}
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid container style={{ padding: "20px" }}>
              <Grid item xs={12} style={{ textAlign: "right" }}>
                <Typography variant="subtitle1">
                  Subtotal: ₹{selectedInvoice.total_amount}
                </Typography>
                <Typography variant="subtitle1">
                  Discount(%): {Number(getInvoiceDiscountPercent(selectedInvoice)).toFixed(2)}
                </Typography>
                <Typography variant="subtitle1">
                  Tax(%): {selectedInvoice.tax}
                </Typography>
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Total: ₹{selectedInvoice.total}
                </Typography>
                <Typography variant="subtitle1">
                  Payment: ₹{selectedInvoice.partial_payment}
                </Typography>
                <Typography variant="subtitle1">
                  Balance Amount: ₹
                  {(
                    selectedInvoice.total - selectedInvoice.partial_payment
                  ).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>

            <Grid container style={{ padding: "20px" }}>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                Invoice Terms:
              </Typography>
              <Typography variant="subtitle1">
                Please pay your invoice before {selectedInvoice.due_date}.
              </Typography>
            </Grid>

            {/* <FooterPdfTemplateWithDept
              departments={departments}
              themeColour={themeColour}
            /> */}
            <Box sx={{ width: "100%", mt: 4 }}>
              <Box
                sx={{
                  bgcolor: themeColour || "#21cdc0",
                  color: "white",
                  p: 1,
                  textAlign: "center",
                }}
              >
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{ textAlign: "center" }}
                >
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                      Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform
                      (visit: https://www.hlthtek.com )
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <img src={Navlogo} alt="hlthtek Logo" style={{ height: "30px" }} />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Card>
        ) : (
          <h1>No invoice selected</h1>
        )}
      </div>
      <InvoicePayment
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        invoice={invoiceForPayment}
        onStatusUpdated={() => {
          // Re-fetch via the same fetchInvoiceList used everywhere else on this
          // screen, preserving the current page/search/date-filter instead of a
          // separate GET call that expected a different response shape
          // (res.data.data/status) than this endpoint actually returns
          // (res.data.invoices) — that mismatch was leaving the list empty
          // after every "Pay Now" action.
          fetchInvoiceList({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
          });
        }}
        onAfterClose={() => {
          setInvoiceForPayment(null);
        }}
      />
      <InvoicePatientSearchDialog
        open={openPatientSearch}
        onClose={() => setOpenPatientSearch(false)}
        authToken={authToken}
        backendUrl={REACT_APP_BACKEND_ROUTE_URL}
        onSelectPatient={handleSelectPatientFilter}
      />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        initialFilters={activeFilters}
        onApply={applyFilters}
      />
    </>
  );
};

export default Invoice;
